import { useDroppable } from '@dnd-kit/core';

import { cn } from '../../../shared/lib/cn';
import type { TFolderCrumb, TFolderScope } from '../../../entities/folder';
import { dropId } from '../lib';

interface ICrumbProps {
  id: string | null;
  label: string;
  current: boolean;
  onFolder: (id: string | null) => void;
}

// Every crumb is a drop target, so a flow dragged out of a deep folder goes up in one move.
const Crumb = ({ id, label, current, onFolder }: ICrumbProps) => {
  const { setNodeRef, isOver } = useDroppable({ id: dropId(id), data: { folderId: id } });
  return (
    <button
      ref={setNodeRef}
      type="button"
      aria-current={current ? 'page' : undefined}
      onClick={() => onFolder(id)}
      className={cn(
        'max-w-crumb truncate rounded-item px-label py-hair text-data',
        current ? 'font-medium text-primary' : 'text-muted hover:text-primary',
        isOver && 'bg-highlight text-primary',
      )}
    >
      {label}
    </button>
  );
};

interface IFolderCrumbsProps {
  scope: TFolderScope;
  path: readonly TFolderCrumb[];
  onFolder: (id: string | null) => void;
}

// Not the scope's name: the page title right above already says "Flows".
const ROOT_CRUMB: Record<TFolderScope, string> = {
  flows: 'All flows',
  library: 'All items',
};

export const FolderCrumbs = ({ scope, path, onFolder }: IFolderCrumbsProps) => (
  <nav aria-label="Folder" className="flex min-w-0 flex-wrap items-center gap-tight">
    <Crumb id={null} label={ROOT_CRUMB[scope]} current={path.length === 0} onFolder={onFolder} />
    {path.map((crumb, index) => (
      <span key={crumb.id} className="flex min-w-0 items-center gap-tight">
        <span aria-hidden className="text-data text-faint">
          /
        </span>
        <Crumb
          id={crumb.id}
          label={crumb.name}
          current={index === path.length - 1}
          onFolder={onFolder}
        />
      </span>
    ))}
  </nav>
);
